import type { Comment } from './comment';
import type { Customer } from './customer';
import type { ArchiveFolder, Document } from './document';
import type { FunctionExecution } from './function-execution';
import type { Position } from './position';
import type { Project } from './project';
import type { Orga } from './orga';
import type { OrgaUser } from './orga-user';
import type { User } from './user';
import type { Work } from './work';
import type { Invoice } from './invoice';
import type { DocumentData, DocumentReference } from 'firebase/firestore';

export type Entities =
  | 'archive'
  | 'position'
  | 'project'
  | 'work'
  | 'comment'
  | 'orga'
  | 'orgaUser'
  | 'user'
  | 'customer-company'
  | 'customer-individual'
  | 'document'
  | 'invoice'
  | 'permission'
  | 'history'
  | 'folder'
  | 'function-execution';

export const ENTITIES_DE: { [key in Entities]: string } = {
  archive: 'Archiv',
  comment: 'Kommentar',
  position: 'Position',
  project: 'Projekt',
  work: 'Arbeit',
  orga: 'Organisation',
  user: 'User',
  ['customer-company']: 'Firmenkunde',
  ['customer-individual']: 'Privatkunde',
  document: 'Dokument',
  invoice: 'Rechnung',
  history: 'History',
  permission: 'Berechtigung',
  orgaUser: 'Organisation User',
  folder: 'Dokumentenordner',
  ['function-execution']: 'Function Execution',
};

export type Id = string;

export interface WithId {
  readonly id: Id;
}

// export interface FirestoreEntity<T extends WithId> {
export interface FirestoreEntity<T> {
  id: string;
  docRef: DocumentReference<T>;
  data: T;
}

export function getEntityDisplayName(data: DocumentData, entity: Entities): string {
  switch (entity) {
    case 'customer-company':
    case 'customer-individual':
      return (data as Customer).displayName;
    case 'comment':
      return (data as Comment).commentText.slice(0, 20);
    case 'document':
      return (data as Document).name;
    case 'invoice':
      return `${ENTITIES_DE['invoice']} ${(data as Invoice).invoiceNumber}`;
    case 'history':
      return '';
    case 'permission':
      return '';
    case 'position':
      return (data as Position).name;
    case 'project':
      return (data as Project).name;
    case 'orga':
      return (data as Orga).name;
    case 'orgaUser':
      return (data as OrgaUser).userDisplayName ?? 'no display name';
    case 'user':
      return (data as User).firstName + (data as User).lastName;
    case 'folder':
      return (data as ArchiveFolder).name;
    case 'work':
      const work = data as Work;
      return work.workMinutes + 'minutes from ' + work.orgaUser.displayName + ' on ' + work.date;
    case 'archive':
      return `Archive`;
    case 'function-execution':
      return (data as FunctionExecution).functionName;
  }
}

export interface LastEditor {
  callId: string;
  authUserUid: string;
  userId: string;
  userDisplayName: string;
  userInitials: string;
}

export interface Summary {
  id: string;
  displayName: string;
}

export type IsoDate = string;
export type IsoDateTime = string;

export interface Address {
  addressLine1: string;
  addressLine2: string;
  zipCode: string;
  city: string;
  countryCode: string;
}

export interface BankConnection {
  name: string;
  iban: string;
  qrIban: string;
  bic: string;
  zipCode: string;
  city: string;
}

export interface InvoiceSettings {
  vatNumber: string;
  defaultVatRate: number;
  defaultPaymentDurationDays: number;
  currentInvoiceNumber: string;
}

export type NumberString = string;
export type VatRateDecimal = string;

export type Nullable<T> = {
  [P in keyof T]: T[P] | null;
};

export type NullablePartial<T> = {
  [P in keyof T]?: T[P] | null;
};
